import React, { useMemo, useState, useEffect } from 'react'
import { router } from '@inertiajs/react'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select'
import { Crown, Loader2, Info, ArrowLeft, ArrowRight } from 'lucide-react'
import { partitionCandidates, createGroupBlocker, countListings, type GroupCandidate } from '@/lib/groups'
import { mappingPayload, parseMappingErrors, type MappingErrorItem, type MappingRequest } from '@/lib/combined-inventory'
import { useRoomMapping } from '@/hooks/useRoomMappingPreview'
import RoomMappingEditor from '@/components/account/groups/RoomMappingEditor'

interface CreateGroupDialogProps {
  /**
   * `/a/:account_slug/settings` — the business's settings root, served by the backend
   * (`currentAccount.settingsPath`). Every endpoint below is this plus a FIXED suffix.
   */
  settingsPath: string
  open: boolean
  /** The account's ungrouped hotels — everything a new group can be built from. */
  candidates: GroupCandidate[]
  onClose: () => void
}

// CREATING A COMBINED INVENTORY (a PropertyGroup in code): choose the lead listing, add the following
// listings. There is no name to give: a combined inventory is one building listed several times, so it
// is always called by its lead listing's name (server: PropertyGroup#name).
//
// The lead is chosen FIRST and everything else follows from it, because the lead is not a label —
// it is the hotel whose saved pricing rules, targets and market analysis will govern every hotel in
// the group, and it is the hotel whose clock and currency the others must share. So members are
// only offered once a lead exists, and a hotel that cannot share the lead's clock or currency is
// shown with the reason rather than hidden (a hotel silently missing from a list is a support
// ticket; a hotel listed with "prices in EUR, but this group prices in GBP" is an answer).
//
// COMBINED INVENTORY. With other hotels attached, a second step maps their rooms: every room of every
// following listing is a Same room of one of the lead's rooms or an Added room, and nothing can be
// created until every room has one (the entry condition; the server refuses it too). The step is the
// server's (RoomMappingEditor); the dialog only carries the decisions to the create request.
export default function CreateGroupDialog({ settingsPath, open, candidates, onClose }: CreateGroupDialogProps) {
  const [leadId, setLeadId] = useState<number | null>(null)
  const [memberIds, setMemberIds] = useState<number[]>([])
  const [submitting, setSubmitting] = useState(false)
  const [step, setStep] = useState<'details' | 'mapping'>('details')
  const [saveErrors, setSaveErrors] = useState<MappingErrorItem[] | null>(null)

  useEffect(() => {
    if (open) return
    setLeadId(null)
    setMemberIds([])
    setSubmitting(false)
    setStep('details')
    setSaveErrors(null)
  }, [open])

  const lead = useMemo(() => candidates.find((candidate) => candidate.id === leadId) ?? null, [candidates, leadId])
  const { eligible, blocked } = useMemo(() => partitionCandidates(candidates, lead), [candidates, lead])

  // Changing the lead can invalidate a hotel already ticked (a different clock or currency), so the
  // selection is pruned to what is still eligible rather than silently submitted and rejected.
  useEffect(() => {
    setMemberIds((previous) => previous.filter((id) => eligible.some((candidate) => candidate.id === id)))
  }, [eligible])

  const blocker = createGroupBlocker(leadId)
  const attachedCount = memberIds.length + (leadId ? 1 : 0)
  const needsMapping = memberIds.length > 0

  // The mapping step's question: this lead, these following listings. Sorted so ticking hotels in a
  // different order asks the same question. Asked as soon as both are chosen, not only on the step, so
  // the step is ready when the operator reaches it and Back then Next keeps their choices.
  const mappingRequest = useMemo<MappingRequest | null>(
    () =>
      open && leadId !== null && needsMapping
        ? { mode: 'join', leadPropertyId: leadId, propertyIds: [...memberIds].sort((a, b) => a - b) }
        : null,
    [open, leadId, needsMapping, memberIds],
  )
  const mapping = useRoomMapping(settingsPath, mappingRequest)

  function toggleMember(id: number, on: boolean) {
    setMemberIds((previous) => (on ? [...previous, id] : previous.filter((memberId) => memberId !== id)))
  }

  function submit() {
    if (blocker || leadId === null) return
    if (needsMapping && (!mapping.canSave || !mapping.decisions)) return
    setSubmitting(true)
    setSaveErrors(null)
    router.post(
      `${settingsPath}/groups`,
      {
        lead_property_id: leadId,
        member_property_ids: memberIds,
        room_mappings: needsMapping && mapping.decisions ? mappingPayload(mapping.decisions) : {},
      },
      {
        onFinish: () => setSubmitting(false),
        onSuccess: onClose,
        // A refused mapping keeps the step open on the rooms it names.
        onError: (errors) =>
          setSaveErrors(
            parseMappingErrors((errors as Record<string, unknown>).roomMapping) ?? [
              { roomTypeId: null, message: 'The combined inventory could not be created. Try again.' },
            ],
          ),
        preserveScroll: true,
      },
    )
  }

  if (step === 'mapping') {
    return (
      <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
        <DialogContent className="sm:max-w-[760px]">
          <DialogHeader>
            <DialogTitle>Map the rooms</DialogTitle>
            <DialogDescription>
              Each room is matched to the same room at {lead?.name ?? 'the lead listing'}, or is an Added room it does not
              have. Check the matches and create.
            </DialogDescription>
          </DialogHeader>

          <DialogBody>
            <RoomMappingEditor
              preview={mapping.preview}
              decisions={mapping.decisions}
              onChange={mapping.setDecisions}
              loading={mapping.loading}
              error={mapping.error}
              saveErrors={saveErrors}
            />
          </DialogBody>

          <DialogFooter className="sm:items-center">
            <Button type="button" variant="ghost" className="rounded-lg" onClick={() => setStep('details')} disabled={submitting}>
              <ArrowLeft className="mr-1.5 h-3.5 w-3.5" />
              Back
            </Button>
            <Button
              type="button"
              className="rounded-lg"
              onClick={submit}
              disabled={submitting || !mapping.canSave}
              aria-busy={submitting}
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                  Creating…
                </>
              ) : (
                <>
                  <Crown className="mr-1.5 h-3.5 w-3.5" />
                  Create combined inventory
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>Create a combined inventory</DialogTitle>
          <DialogDescription>
            For one building listed several times in your PMS. Prices are set at the lead listing, and bookings on every
            listing count toward the building.
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="group-lead">Lead listing</Label>
            <Select value={leadId ? String(leadId) : undefined} onValueChange={(value) => setLeadId(Number(value))}>
              <SelectTrigger id="group-lead">
                <SelectValue placeholder="Choose the lead listing" />
              </SelectTrigger>
              <SelectContent>
                {candidates.map((candidate) => (
                  <SelectItem key={candidate.id} value={String(candidate.id)}>
                    {candidate.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              The combined inventory is called by this listing&apos;s name. Base rates, seasons, guardrails, targets and
              market analysis for the building are set here, and it still publishes its own prices exactly as before.
            </p>
          </div>

          {lead && (
            <div className="space-y-2">
              <Label>Following listings</Label>
              {eligible.length === 0 && blocked.length === 0 ? (
                <p className="rounded-lg border border-dashed border-border bg-muted/30 px-3.5 py-3 text-xs text-muted-foreground">
                  {lead.name} is the only listing available. You can create the combined inventory now and add following
                  listings later.
                </p>
              ) : (
                <div className="divide-y divide-border/50 overflow-hidden rounded-lg border border-border/60">
                  {eligible.map((candidate) => (
                    <label
                      key={candidate.id}
                      className="flex cursor-pointer items-center justify-between gap-3 px-3.5 py-2.5 transition-colors hover:bg-muted/40"
                    >
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-medium text-foreground">{candidate.name}</span>
                        <span className="block text-xs text-muted-foreground">
                          {candidate.currency} · {candidate.timezone}
                        </span>
                      </span>
                      <Switch
                        checked={memberIds.includes(candidate.id)}
                        onCheckedChange={(on) => toggleMember(candidate.id, on)}
                        aria-label={`Add ${candidate.name} as a following listing`}
                      />
                    </label>
                  ))}
                  {blocked.map(({ candidate, reason }) => (
                    <div key={candidate.id} className="bg-muted/30 px-3.5 py-2.5">
                      <p className="truncate text-sm font-medium text-muted-foreground">{candidate.name}</p>
                      <p className="mt-0.5 flex items-start gap-1.5 text-xs text-muted-foreground">
                        <Info className="mt-0.5 h-3 w-3 shrink-0" />
                        <span>{reason}</span>
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* What it will actually do, once there is more than one listing. */}
          {lead && attachedCount > 1 && (
            <p className="rounded-lg border border-primary/20 bg-primary/5 px-3.5 py-2.5 text-xs text-foreground">
              Prices for {countListings(attachedCount)} will be set at {lead.name}, and bookings on every listing will
              count toward the building.
            </p>
          )}
          {lead && attachedCount === 1 && (
            <p className="rounded-lg border border-border/60 bg-muted/40 px-3.5 py-2.5 text-xs text-muted-foreground">
              {lead.name} will be the only listing, so it prices exactly as it does today. You can add following
              listings at any time.
            </p>
          )}
        </DialogBody>

        <DialogFooter>
          <Button type="button" variant="ghost" className="rounded-lg" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          {needsMapping ? (
            <Button
              type="button"
              className="rounded-lg"
              onClick={() => setStep('mapping')}
              disabled={blocker !== null}
              title={blocker ?? undefined}
            >
              Next: map the rooms
              <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
            </Button>
          ) : (
            <Button
              type="button"
              className="rounded-lg"
              onClick={submit}
              disabled={submitting || blocker !== null}
              title={blocker ?? undefined}
              aria-busy={submitting}
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                  Creating…
                </>
              ) : (
                <>
                  <Crown className="mr-1.5 h-3.5 w-3.5" />
                  Create combined inventory
                </>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
